import {
  AlertCircle,
  BarChart3,
  ChevronDown,
  DollarSign,
  Eye,
  MousePointer,
  Percent,
  Search,
  Star,
  Tag,
  Target,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { googleAdsGetKeywordDetails } from 'sprites-types/workflow/tools/googleAdsGetKeywordDetails'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function KeywordItem({ keyword, className }: { keyword: any; className?: string }) {
  // Extract Google Ads keyword performance data
  const keywordText = keyword.keyword || 'Keyword'
  const metrics = keyword.metrics || {}
  const matchType = keyword.matchType || null
  const adGroup = keyword.adGroup || null
  const campaign = keyword.campaign || null
  const currencyCode = keyword.currencyCode || 'USD'

  // Performance metrics
  const impressions = metrics.impressions || 0
  const clicks = metrics.clicks || 0
  const ctr = metrics.ctr || 0
  const averageCpc = metrics.averageCpc || 0
  const cost = metrics.cost || 0
  const conversions = metrics.conversions || 0
  const conversionValue = metrics.conversionValue || null
  const roas = metrics.roas || null
  const searchImpressionShare = metrics.searchImpressionShare || null
  const averagePosition = metrics.averagePosition || null
  const qualityScore = metrics.qualityScore || null
  // Convert status to proper string (handle enum numbers from API)
  const statusRaw = keyword.status
  let status: string | null = null
  if (statusRaw != null) {
    const statusStr = String(statusRaw)
    // Map common enum values to readable status
    if (statusStr === '2' || statusStr === 'ENABLED') {
      status = 'ENABLED'
    } else if (statusStr === '3' || statusStr === 'PAUSED') {
      status = 'PAUSED'
    } else if (statusStr === '4' || statusStr === 'REMOVED') {
      status = 'REMOVED'
    } else if (statusStr === '1' || statusStr === 'UNKNOWN') {
      status = 'UNKNOWN'
    } else if (statusStr === '0' || statusStr === 'UNSPECIFIED') {
      status = 'UNSPECIFIED'
    } else {
      // Use the string as-is if it's already a valid status string
      status = statusStr
    }
  }

  // Calculate derived metrics
  const conversionRate = clicks > 0 ? (conversions / clicks) * 100 : 0
  const cpa = conversions > 0 ? cost / conversions : 0

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  const formatCurrency = (amount: number, currency: string = currencyCode) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`
  }

  const getMatchTypeColor = (matchType: string | null) => {
    switch (matchType) {
      case 'EXACT':
        return 'bg-blue-100 text-blue-700'
      case 'PHRASE':
        return 'bg-purple-100 text-purple-700'
      case 'BROAD':
        return 'bg-orange-100 text-orange-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const getQualityScoreColor = (score: number | null) => {
    if (!score) return 'text-gray-500'
    if (score >= 8) return 'text-green-600'
    if (score >= 6) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getStatusColor = (status: string | null) => {
    if (!status) return 'bg-gray-100 text-gray-700'
    switch (status) {
      case 'ENABLED':
        return 'bg-green-100 text-green-700'
      case 'PAUSED':
        return 'bg-yellow-100 text-yellow-700'
      case 'REMOVED':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const formatROAS = (roas: number | null) => {
    if (roas === null) return null
    return `${roas.toFixed(2)}x`
  }

  const formatImpressionShare = (share: number | null) => {
    if (share === null) return null
    return `${(share * 100).toFixed(1)}%`
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0">
            <Search className="h-4 w-4 text-blue-600" />
          </div>

          <div className="min-w-0 flex-1">
            {/* Keyword, Match Type, and Status */}
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <div className="text-sm font-medium text-gray-900">{keywordText}</div>
              {matchType && (
                <div
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${getMatchTypeColor(matchType)}`}
                >
                  {matchType}
                </div>
              )}
              {status && (
                <div
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(status)}`}
                >
                  {status}
                </div>
              )}
            </div>

            {/* Campaign and Ad Group Info */}
            {(campaign || adGroup) && (
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                {campaign && (
                  <div className="flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    <span className="font-medium">Campaign:</span>
                    <span className="truncate">{campaign}</span>
                  </div>
                )}
                {adGroup && (
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Ad group:</span>
                    <span className="truncate">{adGroup}</span>
                  </div>
                )}
              </div>
            )}

            {/* Performance Metrics Grid */}
            <div className="mb-2 grid grid-cols-2 gap-2 text-xs">
              {/* Impressions */}
              {impressions > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-gray-50 px-2 py-1">
                  <Eye className="h-3 w-3 text-gray-600" />
                  <span className="text-gray-700">{formatNumber(impressions)}</span>
                  <span className="text-gray-500">impressions</span>
                </div>
              )}

              {/* Clicks */}
              {clicks > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-gray-50 px-2 py-1">
                  <MousePointer className="h-3 w-3 text-gray-600" />
                  <span className="text-gray-700">{formatNumber(clicks)}</span>
                  <span className="text-gray-500">clicks</span>
                </div>
              )}

              {/* CTR */}
              {ctr > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-gray-50 px-2 py-1">
                  <Percent className="h-3 w-3 text-gray-600" />
                  <span className="text-gray-700">{formatPercentage(ctr)}</span>
                  <span className="text-gray-500">CTR</span>
                </div>
              )}

              {/* CPC */}
              {averageCpc > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-gray-50 px-2 py-1">
                  <DollarSign className="h-3 w-3 text-gray-600" />
                  <span className="text-gray-700">{formatCurrency(averageCpc)}</span>
                  <span className="text-gray-500">CPC</span>
                </div>
              )}

              {/* Cost */}
              {cost > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-gray-50 px-2 py-1">
                  <DollarSign className="h-3 w-3 text-gray-600" />
                  <span className="text-gray-700">{formatCurrency(cost)}</span>
                  <span className="text-gray-500">cost</span>
                </div>
              )}

              {/* Conversions */}
              {conversions > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-green-50 px-2 py-1">
                  <TrendingUp className="h-3 w-3 text-green-600" />
                  <span className="font-medium text-green-700">{formatNumber(conversions)}</span>
                  <span className="text-green-600">conversions</span>
                </div>
              )}

              {/* Conversion Rate */}
              {conversionRate > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-gray-50 px-2 py-1">
                  <Percent className="h-3 w-3 text-gray-600" />
                  <span className="text-gray-700">{formatPercentage(conversionRate)}</span>
                  <span className="text-gray-500">conv. rate</span>
                </div>
              )}

              {/* CPA */}
              {cpa > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-gray-50 px-2 py-1">
                  <DollarSign className="h-3 w-3 text-gray-600" />
                  <span className="text-gray-700">{formatCurrency(cpa)}</span>
                  <span className="text-gray-500">CPA</span>
                </div>
              )}

              {/* Conversion Value */}
              {conversionValue !== null && conversionValue > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-green-50 px-2 py-1">
                  <DollarSign className="h-3 w-3 text-green-600" />
                  <span className="font-medium text-green-700">
                    {formatCurrency(conversionValue)}
                  </span>
                  <span className="text-green-600">revenue</span>
                </div>
              )}

              {/* ROAS */}
              {roas !== null && roas > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-blue-50 px-2 py-1">
                  <Target className="h-3 w-3 text-blue-600" />
                  <span className="font-medium text-blue-700">{formatROAS(roas)}</span>
                  <span className="text-blue-600">ROAS</span>
                </div>
              )}

              {/* Search Impression Share */}
              {searchImpressionShare !== null && (
                <div className="flex items-center gap-1.5 rounded bg-purple-50 px-2 py-1">
                  <BarChart3 className="h-3 w-3 text-purple-600" />
                  <span className="text-purple-700">
                    {formatImpressionShare(searchImpressionShare)}
                  </span>
                  <span className="text-purple-600">imp. share</span>
                </div>
              )}

              {/* Average Position */}
              {averagePosition !== null && averagePosition > 0 && (
                <div className="flex items-center gap-1.5 rounded bg-gray-50 px-2 py-1">
                  <BarChart3 className="h-3 w-3 text-gray-600" />
                  <span className="text-gray-700">{averagePosition.toFixed(1)}</span>
                  <span className="text-gray-500">avg. pos.</span>
                </div>
              )}
            </div>

            {/* Quality Score */}
            {qualityScore !== null && (
              <div className="flex items-center gap-1.5">
                <Star className={`h-3 w-3 ${getQualityScoreColor(qualityScore)}`} />
                <span className="text-xs text-gray-600">Quality Score:</span>
                <span className={`text-xs font-semibold ${getQualityScoreColor(qualityScore)}`}>
                  {qualityScore}/10
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function GoogleAdsGetKeywordDetailsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsGetKeywordDetails, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const query = inputs.args?.query || inputs.query || inputs.search || null
  const keywords = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.keywords ||
      (result as any)?.suggestions ||
      (result ? [result] : [])

  // Calculate totals for summary
  const totalImpressions = keywords.reduce(
    (sum: number, k: any) => sum + ((k.metrics?.impressions || 0) as number),
    0
  )
  const totalClicks = keywords.reduce(
    (sum: number, k: any) => sum + ((k.metrics?.clicks || 0) as number),
    0
  )
  const totalCost = keywords.reduce(
    (sum: number, k: any) => sum + ((k.metrics?.cost || 0) as number),
    0
  )
  const totalConversions = keywords.reduce(
    (sum: number, k: any) => sum + ((k.metrics?.conversions || 0) as number),
    0
  )
  const totalConversionValue = keywords.reduce(
    (sum: number, k: any) => sum + ((k.metrics?.conversionValue || 0) as number),
    0
  )
  const totalROAS =
    totalCost > 0 && totalConversionValue > 0 ? totalConversionValue / totalCost : null
  const averageCTR = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0
  const averageCPC = totalClicks > 0 ? totalCost / totalClicks : 0

  if (keywords.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-start gap-4 px-4 py-4">
          <div className="mt-0.5 flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 text-sm font-semibold text-gray-900">No keyword data found</div>
            <div className="space-y-1 text-xs text-gray-600">
              {query ? (
                <>
                  <p>No keywords found matching &quot;{query}&quot; in the last 30 days.</p>
                  <p className="mt-2 text-gray-500">
                    <strong>Possible reasons:</strong>
                  </p>
                  <ul className="ml-2 list-inside list-disc space-y-0.5 text-gray-500">
                    <li>The search query doesn&apos;t match any active keywords</li>
                    <li>Keywords exist but have no impressions in the last 30 days</li>
                    <li>Keywords may be paused or removed</li>
                  </ul>
                </>
              ) : (
                <>
                  <p>No keyword performance data found for the last 30 days.</p>
                  <p className="mt-2 text-gray-500">
                    <strong>Possible reasons:</strong>
                  </p>
                  <ul className="ml-2 list-inside list-disc space-y-0.5 text-gray-500">
                    <li>No active keywords with impressions in your Google Ads account</li>
                    <li>All keywords may be paused or have zero impressions</li>
                    <li>Date range may need adjustment (currently showing last 30 days)</li>
                    <li>Campaigns may not be running or may have just started</li>
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (keywords.length === 1) {
    return <KeywordItem keyword={keywords[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                Google Ads Keyword Details
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {keywords.length} keywords
                {query && ` • "${query}"`}
                {totalImpressions > 0 && ` • ${totalImpressions.toLocaleString()} impressions`}
                {totalClicks > 0 && ` • ${totalClicks.toLocaleString()} clicks`}
                {averageCTR > 0 && ` • ${averageCTR.toFixed(2)}% CTR`}
                {averageCPC > 0 &&
                  ` • ${new Intl.NumberFormat('en-US', { style: 'currency', currency: keywords[0]?.currencyCode || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(averageCPC)} avg CPC`}
                {totalConversions > 0 && ` • ${totalConversions.toLocaleString()} conversions`}
                {totalConversionValue > 0 &&
                  ` • ${new Intl.NumberFormat('en-US', { style: 'currency', currency: keywords[0]?.currencyCode || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalConversionValue)} revenue`}
                {totalROAS !== null && totalROAS > 0 && ` • ${totalROAS.toFixed(2)}x ROAS`}
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {keywords.map((keyword: any, index: number) => (
            <KeywordItem
              key={keyword.keyword || keyword.text || index}
              keyword={keyword}
              className="mx-8"
            />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadGoogleAdsCampaignKeywordPerformance(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsGetKeywordDetailsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsGetKeywordDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const query = inputs.args?.query || inputs.query || inputs.search || null
  const keywords = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.keywords ||
      (result as any)?.suggestions ||
      (result ? [result] : [])

  if (keywords.length === 0) {
    return `## Google Ads Keyword Details

${query ? `**Query:** "${query}"` : '**All Keywords**'}

No keywords found.

---
`
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const keywordsMarkdown = keywords
    .map((keyword: any) => {
      const keywordText = keyword.keyword || 'Keyword'
      const metrics = keyword.metrics || {}
      const matchType = keyword.matchType || null
      const adGroup = keyword.adGroup || null
      const campaign = keyword.campaign || null
      const currencyCode = keyword.currencyCode || 'USD'

      const impressions = metrics.impressions || 0
      const clicks = metrics.clicks || 0
      const ctr = metrics.ctr || 0
      const averageCpc = metrics.averageCpc || 0
      const cost = metrics.cost || 0
      const conversions = metrics.conversions || 0
      const conversionValue = metrics.conversionValue || null
      const valuePerConversion = metrics.valuePerConversion || null
      const roas = metrics.roas || null
      const searchImpressionShare = metrics.searchImpressionShare || null
      const averagePosition = metrics.averagePosition || null
      const qualityScore = metrics.qualityScore || null
      const status = keyword.status || null
      const conversionRate = clicks > 0 ? (conversions / clicks) * 100 : 0
      const cpa = conversions > 0 ? cost / conversions : 0

      // Build markdown lines array to avoid blank lines from null/empty fields
      const lines: string[] = [`### ${keywordText}`, '']

      // Add optional metadata fields
      if (matchType) lines.push(`**Match Type:** ${matchType}`)
      if (status) lines.push(`**Status:** ${status}`)
      if (campaign) lines.push(`**Campaign:** ${campaign}`)
      if (adGroup) lines.push(`**Ad Group:** ${adGroup}`)

      // Add performance metrics section
      if (lines.length > 2) lines.push('') // Add blank line before metrics if metadata exists
      lines.push('**Performance Metrics:**')
      lines.push(`- **Impressions:** ${impressions.toLocaleString()}`)
      lines.push(`- **Clicks:** ${clicks.toLocaleString()}`)
      lines.push(`- **CTR:** ${ctr.toFixed(2)}%`)
      lines.push(`- **CPC:** ${formatCurrency(averageCpc, currencyCode)}`)
      lines.push(`- **Cost:** ${formatCurrency(cost, currencyCode)}`)
      lines.push(`- **Conversions:** ${conversions.toLocaleString()}`)

      // Add optional performance metrics
      if (conversionRate > 0) lines.push(`- **Conversion Rate:** ${conversionRate.toFixed(2)}%`)
      if (cpa > 0) lines.push(`- **CPA:** ${formatCurrency(cpa, currencyCode)}`)
      if (conversionValue !== null && conversionValue > 0)
        lines.push(`- **Conversion Value:** ${formatCurrency(conversionValue, currencyCode)}`)
      if (valuePerConversion !== null && valuePerConversion > 0)
        lines.push(
          `- **Value per Conversion:** ${formatCurrency(valuePerConversion, currencyCode)}`
        )
      if (roas !== null && roas > 0) lines.push(`- **ROAS:** ${roas.toFixed(2)}x`)
      if (searchImpressionShare !== null)
        lines.push(`- **Search Impression Share:** ${(searchImpressionShare * 100).toFixed(1)}%`)
      if (averagePosition !== null && averagePosition > 0)
        lines.push(`- **Average Position:** ${averagePosition.toFixed(1)}`)
      if (qualityScore !== null) lines.push(`- **Quality Score:** ${qualityScore}/10`)

      return lines.join('\n')
    })
    .join('\n---\n\n')

  return `## Google Ads Keyword Details (${keywords.length} keywords)

${query ? `**Query:** "${query}"` : '**All Keywords**'}

${keywordsMarkdown}

---
`
}

export const GoogleAdsGetKeywordDetailsDisplay = createWorkflowToolComponent(
  GoogleAdsGetKeywordDetailsDisplayComponent,
  downloadGoogleAdsCampaignKeywordPerformance,
  googleAdsGetKeywordDetailsToMarkdown
)
